import React, { useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  Easing,
} from "react-native-reanimated";
import Button from "@components/Button";
import { scale } from "@utils/scale";
import ChevronDownIcon from "@assets/svg/ChevronDownIcon";
import WheelPicker from "@components/WheelPicker";
import t from "@constants/localization";
import MessageWrapper from "./MessageWrapper";

const PATH = "screens/CLFlow/components/CustomWheelPicker";
const DEFAULT_AGE = 25;

const CustomWheelPicker = ({ item, onSubmit, value }) => {
  const [newValue, setValue] = useState(value || DEFAULT_AGE);
  const [height, setHeight] = useState(0);
  const [isCollapsed, setCollapsed] = useState(true);

  const animatedHeight = useSharedValue(0);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      height: withTiming(animatedHeight.value, {
        duration: 250,
        easing: Easing.out(Easing.ease),
      }),
    };
  });

  React.useEffect(() => {
    if (isCollapsed) {
      animatedHeight.value = 0;
    } else {
      animatedHeight.value = height;
    }
  }, [isCollapsed, height, animatedHeight]);

  React.useEffect(() => {
    if (value !== null) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [value]);

  const onPress = React.useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const onLayout = (event) => {
    const measuredHeight = event.nativeEvent.layout.height;
    setHeight(measuredHeight);
  };

  const handleOnSubmit = () => {
    onPress();
    onSubmit(item?.isLast, { [item.name]: newValue });
  };

  return (
    <MessageWrapper>
      <View style={styles.container}>
        <View style={styles.questionContainer}>
          <Text style={styles.questionText}>{item?.heading}</Text>
          <TouchableOpacity style={styles.collapse_view} onPress={onPress}>
            <ChevronDownIcon />
          </TouchableOpacity>
        </View>
        <View style={styles.dateContainer}>
          {isCollapsed && (
            <View style={styles.dateTextContainer}>
              <Text style={styles.dateText}>{value || "-"}</Text>
            </View>
          )}
          <Animated.View style={[animatedStyle, styles.animatedContainer]}>
            <View onLayout={onLayout} style={styles.datePickerContainer}>
              <WheelPicker
                data={item.options}
                onChange={setValue}
                title={t(`${PATH}/year`)}
                value={value === null ? DEFAULT_AGE : value}
              />
            </View>
          </Animated.View>
        </View>
      </View>
      {!isCollapsed && (
        <Button
          btnText={t(`${PATH}/submit`)}
          buttonFontType="bold"
          fontSize={scale(14)}
          buttonWidth="95%"
          onPress={handleOnSubmit}
          height={scale(50)}
          borderRadius={8}
          textStyle={{ fontSize: scale(16), lineHeight: 22 }}
          containerStyle={{ alignSelf: "center", marginTop: scale(10) }}
        />
      )}
    </MessageWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#ffffff",
    borderRadius: 5,
    padding: 10,
    marginHorizontal: 10,
    overflow: "hidden",
  },
  collapse_view: { padding: 10, flex: 1, alignItems: "flex-end" },
  questionContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: 10,
  },
  dateTextContainer: {
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    borderRadius: 8,
    padding: 10,
    width: "90%",
  },
  dateText: {
    fontFamily: "NotoSansDevanagari-Regular",
    fontWeight: "400",
    fontSize: 14,
  },
  questionText: {
    fontSize: 16,
    fontFamily: "NotoSansDevanagari-SemiBold",
    fontWeight: "600",
  },
  dateContainer: {
    marginTop: 12,
    alignItems: "center",
    marginBottom: 10,
  },
  animatedContainer: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  datePickerContainer: {
    position: "absolute",
    alignItems: "center",
  },
});

export default CustomWheelPicker;
